import React, { useEffect, useState } from "react";
import { useForm, FieldErrors } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button, SpinningButton } from "Components/ReactstrapSlim";
import { InputFL, ValidationSummary } from "Components/Inputs";
import { AccountClient, IForgotPasswordRequest, ForgotPasswordRequest } from "Services/API/API";
import ReCAPTCHA from "react-google-recaptcha";
import { SigningEmailProps, useSigningEmailEffect } from "./SigningCommon";
import { useApiClient } from "Services/API/useApiClient";

interface ForgotPasswordFormModel {
   email: string,
}

const ForgotPasswordFormSchema: yup.SchemaOf<ForgotPasswordFormModel> = yup.object({
   email: yup.string()
      .required("Email is required")
      .email("Email is not valid")
      .defined(),
}).defined();



export interface ForgotPasswordFormProps extends SigningEmailProps {
   isActive: boolean;
   onSwitchBack: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = function ({ isActive, onSwitchBack, ...signingEmailProps }) {
   const accountClient = useApiClient(AccountClient);
   const [isLoading, setIsLoading] = useState<boolean>(false);
   const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
   const [serverErrors, setServerErrors] = useState<Array<[string, string]>>([]);
   const [recaptchaToken, setRecaptchaToken] = useState<string | undefined>(undefined);

   const formMethods = useForm<ForgotPasswordFormModel>({
      defaultValues: {
         email: signingEmailProps.email,
      },
      resolver: yupResolver(ForgotPasswordFormSchema),
      mode: "onBlur"
   });

   useSigningEmailEffect(signingEmailProps, formMethods, "email");

   const onSubmit = async () => {
      if (isLoading)
         return;
      setIsLoading(true);
      setServerErrors([]);
      try {
         if (!recaptchaToken) {
            setServerErrors([["email", "Please confirm you are not a robot"]]);
            return;
         }
         const isValid = await formMethods.trigger();
         if (isValid) {
            const formValues = formMethods.getValues();

            const forgotPasswordRequest: Partial<IForgotPasswordRequest> = {
               ...formValues,
               recaptchaToken
            };
            const forgotResult = await accountClient.forgotPassword(ForgotPasswordRequest.fromJS(forgotPasswordRequest));
            if (forgotResult.success) {
               setIsSubmitted(true);
            } else if (forgotResult.errorMessage) {
               setServerErrors([["email", forgotResult.errorMessage]]);
            }
         }
         else {
            let name: keyof FieldErrors<ForgotPasswordFormModel>;
            for (name in formMethods.formState.errors) {
               formMethods.setFocus(name);
               break;
            }
         }
      } catch (err: any) {
         if (err.error) {
            setServerErrors([["email", err.error]]);
         }
         console.log(err);
      } finally {
         setIsLoading(false);
      }
   }

   const submitOnEnterHandler = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
         onSubmit();
      }
   }

   const onBackClick = (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      setIsSubmitted(false);
      onSwitchBack();
   }

   useEffect(() => {
      if (isActive) {
         setTimeout(function () {
            formMethods.setFocus("email");
         }, 500);
      }
   }, [isActive])

   return (
      <div className="signing-mode-inner signing-mode-inner--centered">
         {
            isSubmitted
               ? <>
                  <div className="signing-forgot-confirmation">
                     <div className="signing-forgot-confirmation-icon"></div>
                     <div className="signing-forgot-confirmation-text">Reset password instructions were sent to your email</div>
                     <Button className="signing-forgot-confirmation-cta" onClick={onBackClick} color="primary" size="md" fluid>Got it</Button>
                  </div>
               </>
               : <>
                  <div className="signing-header">
                     Forgot password
                  </div>
                  <div className="signing-caption">
                     We will send you an email with instructions
                     to the email you used when you joined LiquidSpace.
                  </div>
                  <div className="signing-forgot-password-form">
                     <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverErrors} className="text-center" />
                     <InputFL control={formMethods.control} name="email" placeholder="Email" className="sfpf-input" autoComplete="off" onKeyDown={submitOnEnterHandler} />
                     <div className="sfpf-recaptcha">
                        <ReCAPTCHA
                           sitekey={LiquidSpaceConfig.RecaptchaKeys.Explicit.SiteKey!}
                           onChange={(token) => setRecaptchaToken(token || undefined)}
                        />
                     </div>
                     <SpinningButton isLoading={isLoading} onClick={onSubmit} color="primary" size="md" fluid className="sfpf-cta">Reset password</SpinningButton>
                     <div className="signing-mode-switcher">Remembered? <a href="#" onClick={onBackClick}>Go back</a></div>
                  </div>
               </>
         }
      </div>
   )
}